import React, { useState, useRef, DragEvent, ChangeEvent, useEffect } from 'react';
import { Upload, FileText, AlertCircle, Lock } from 'lucide-react';
import { DocumentType } from '../../types/document';
import useDocumentUpload from '../../hooks/useDocumentUpload';
import useDocumentStore from '../../stores/documentStore';
import DocumentTypeModal from './DocumentTypeModal';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { useEnhancedDocumentAnalytics } from '../../hooks/useEnhancedDocumentAnalytics';
import './DocumentUploadZone.css';

interface DocumentUploadZoneProps {
  courseId?: string;
  onUploadComplete?: () => void;
  showAlways?: boolean;
  onFileInputRef?: (ref: HTMLInputElement | null) => void;
  uploadHandlers?: {
    uploadQueue: any[];
    startUpload: (files: File[], documentType?: DocumentType) => void;
    cancelUpload: (uploadId: string) => void;
    retryUpload: (uploadId: string) => void;
    removeFromQueue: (uploadId: string) => void;
  };
}

const DocumentUploadZone: React.FC<DocumentUploadZoneProps> = ({ 
  courseId, 
  onUploadComplete,
  showAlways = false,
  onFileInputRef,
  uploadHandlers
}) => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const { showToast } = useToast();
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingOverDocument, setIsDraggingOverDocument] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  
  // Use provided upload handlers or create own instance
  const ownUploadHandlers = useDocumentUpload();
  const { uploadQueue, startUpload, cancelUpload, retryUpload, removeFromQueue } = uploadHandlers || ownUploadHandlers;
  const userQuota = useDocumentStore(state => state.userQuota);
  
  // Enhanced Analytics hooks
  const { 
    trackUploadZoneAccessed, 
    trackDocumentUploadStart,
    trackDocumentUploadComplete,
    trackDocumentUploadFailed 
  } = useEnhancedDocumentAnalytics();
  
  // Track upload queue changes for completion/error events
  const prevUploadQueue = useRef<typeof uploadQueue>([]);
  useEffect(() => {
    const prevQueue = prevUploadQueue.current;
    const currentQueue = uploadQueue;
    
    // Find items that changed status
    currentQueue.forEach(currentItem => {
      const prevItem = prevQueue.find(item => item.id === currentItem.id);
      
      // Skip if no change or if this is a new item (already tracked in start)
      if (!prevItem || prevItem.status === currentItem.status) return;
      
      // Track completion
      if (currentItem.status === 'completed' && prevItem.status !== 'completed') {
        const processingTime = Date.now() - (currentItem.startTime || Date.now());
        trackDocumentUploadComplete(
          currentItem.file.name, 
          currentItem.file.size,
          processingTime, 
          processingTime, // processing time same as total for now
          currentItem.documentId
        );
      }
      
      // Track errors
      if (currentItem.status === 'error' && prevItem.status !== 'error') {
        trackDocumentUploadFailed(
          currentItem.file.name, 
          'upload_error',
          currentItem.error || 'Unknown upload error',
          'click_upload',
          'upload'
        );
      }
    });
    
    // Update previous queue reference
    prevUploadQueue.current = [...currentQueue];
  }, [uploadQueue, trackDocumentUploadComplete, trackDocumentUploadFailed]);

  // Share file input ref with parent
  useEffect(() => {
    if (onFileInputRef && fileInputRef.current) {
      onFileInputRef(fileInputRef.current);
    }
  }, [onFileInputRef]);

  // Set up global drag event listeners
  useEffect(() => {
    let dragTimeout: NodeJS.Timeout;
    let lastTarget: EventTarget | null = null;

    const handleGlobalDragEnter = (e: DragEvent) => {
      lastTarget = e.target;
      clearTimeout(dragTimeout);
      
      // Check if we're dragging over the sidebar and ignore if so
      const target = e.target as Element;
      const sidebar = document.querySelector('[class*="sidebar"]');
      if (sidebar && sidebar.contains(target)) {
        return;
      }
      
      const dt = e.dataTransfer;
      if (dt && dt.types && (dt.types.includes('Files') || dt.types.indexOf('Files') !== -1)) {
        e.preventDefault();
        setIsDraggingOverDocument(true);
      }
    };

    const handleGlobalDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleGlobalDragLeave = (e: DragEvent) => {
      if (e.target === lastTarget || e.target === document) {
        dragTimeout = setTimeout(() => {
          setIsDraggingOverDocument(false);
        }, 100);
      }
    };

    const handleGlobalDrop = (e: DragEvent) => {
      // Prevent default to stop browser from opening the file
      e.preventDefault();
      clearTimeout(dragTimeout);
      // Don't hide overlay here - let the overlay's drop handler do it
    };

    // Use capture phase to catch events early
    document.addEventListener('dragenter', handleGlobalDragEnter as any, true);
    document.addEventListener('dragover', handleGlobalDragOver as any, true);
    document.addEventListener('dragleave', handleGlobalDragLeave as any, true);
    document.addEventListener('drop', handleGlobalDrop as any, true);

    return () => {
      clearTimeout(dragTimeout);
      document.removeEventListener('dragenter', handleGlobalDragEnter as any, true);
      document.removeEventListener('dragover', handleGlobalDragOver as any, true);
      document.removeEventListener('dragleave', handleGlobalDragLeave as any, true);
      document.removeEventListener('drop', handleGlobalDrop as any, true);
    };
  }, []);

  const validateFiles = (files: FileList | File[]): File[] => {
    const validFiles: File[] = [];
    const errors: string[] = [];
    const rejectedFiles: { name: string; reason: string }[] = [];
    
    const fileArray = Array.from(files);
    
    const validExtensions = [
      // Document formats
      '.pdf', '.doc', '.docx', '.txt', '.rtf',
      // Spreadsheet formats
      '.xls', '.xlsx', '.csv',
      // Presentation formats
      '.ppt', '.pptx',
      // Image formats
      '.jpg', '.jpeg', '.png', '.gif', '.webp',
      // Data formats
      '.json', '.xml', '.yaml', '.yml',
      // Code files
      '.py', '.js', '.ts', '.java', '.cpp', '.c', '.cs', '.rb', '.go', '.rs',
      '.html', '.css', '.jsx', '.tsx', '.vue', '.php', '.swift', '.kt',
      // Text formats
      '.md', '.markdown', '.tex', '.latex'
    ];
    
    fileArray.forEach(file => {
      // Check file size
      if (file.size > 50 * 1024 * 1024) {
        errors.push(`${file.name} exceeds 50MB limit`);
        rejectedFiles.push({ name: file.name, reason: 'File size exceeds 50MB limit' });
        return;
      }
      
      if (file.size === 0) {
        errors.push(`${file.name} is empty`);
        rejectedFiles.push({ name: file.name, reason: 'File is empty' });
        return;
      }
      
      // Check file extension
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!validExtensions.includes(fileExtension)) {
        errors.push(`${file.name} has unsupported file type`);
        rejectedFiles.push({ 
          name: file.name, 
          reason: `File type ${fileExtension} is not supported` 
        });
        return;
      }
      
      validFiles.push(file);
    });
    
    // Show toast notifications for rejected files
    if (rejectedFiles.length > 0) {
      if (rejectedFiles.length === 1) {
        showToast({
          type: 'error',
          title: 'File not supported',
          message: `${rejectedFiles[0].name}: ${rejectedFiles[0].reason}`,
          duration: 6000
        });
      } else {
        const fileList = rejectedFiles.map(f => `• ${f.name}: ${f.reason}`).join('\n');
        showToast({
          type: 'error',
          title: `${rejectedFiles.length} files not supported`,
          message: fileList,
          duration: 8000
        });
      }
      
      // Also show supported file types
      showToast({
        type: 'info',
        title: 'Supported file types',
        message: 'PDF, Word, Excel, PowerPoint, Images (JPG/PNG), Text files, Code files, JSON, XML, Markdown',
        duration: 10000
      });
    }
    
    if (errors.length > 0) {
      console.error('File validation errors:', errors);
    }
    
    return validFiles;
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    // Check authentication before processing dropped files
    if (!isAuthenticated) {
      console.error('User not authenticated for file upload');
      showToast({
        type: 'warning',
        title: 'Authentication required',
        message: 'Please log in to upload documents',
        duration: 5000
      });
      return;
    }
    
    const files = e.dataTransfer.files;
    const validFiles = validateFiles(files);
    
    if (validFiles.length > 0) {
      // Track drag-and-drop upload initiation
      trackDocumentUploadStart(validFiles, 'drag_drop');
      
      setPendingFiles(validFiles);
      setShowTypeModal(true);
    }
  };

  const handleOverlayDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Check authentication before processing dropped files
    if (!isAuthenticated) {
      console.error('User not authenticated for file upload');
      setIsDraggingOverDocument(false);
      showToast({
        type: 'warning',
        title: 'Authentication required',
        message: 'Please log in to upload documents',
        duration: 5000
      });
      return;
    }
    
    const files = e.dataTransfer.files;
    const validFiles = validateFiles(files);
    
    if (validFiles.length > 0) {
      // Track overlay drag-and-drop upload initiation
      trackDocumentUploadStart(validFiles, 'drag_drop');
      
      setPendingFiles(validFiles);
      setShowTypeModal(true);
    }
    
    // Hide overlay after drop
    setIsDraggingOverDocument(false);
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    
    // Check authentication before processing files
    if (!isAuthenticated) {
      console.error('User not authenticated for file upload');
      showToast({
        type: 'warning',
        title: 'Authentication required',
        message: 'Please log in to upload documents',
        duration: 5000
      });
      return;
    }
    
    const validFiles = validateFiles(files);
    
    if (validFiles.length > 0) {
      // Track click upload initiation
      trackDocumentUploadStart(validFiles, 'click_upload');
      
      setPendingFiles(validFiles);
      setShowTypeModal(true);
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleTypeSelect = async (type: DocumentType | null) => {
    console.log('[DocumentUploadZone] Document type selected:', type);
    console.log('[DocumentUploadZone] Files to upload:', pendingFiles.map(f => ({
      name: f.name,
      size: f.size,
      type: f.type
    })));
    
    setShowTypeModal(false);
    
    // Double-check authentication before starting upload
    if (!isAuthenticated) {
      console.error('[DocumentUploadZone] User not authenticated for file upload');
      setPendingFiles([]);
      return;
    }
    
    console.log('[DocumentUploadZone] Starting upload process...');
    
    // Track document type selection
    pendingFiles.forEach(file => {
      // Additional tracking for type selection (success path continues in startUpload)
      console.log(`[DocumentUploadZone] File ${file.name} assigned document type: ${type || 'auto'}`);
    });
    
    startUpload(pendingFiles, type || undefined);
    
    setPendingFiles([]);
    
    if (onUploadComplete) {
      console.log('[DocumentUploadZone] Calling onUploadComplete callback');
      onUploadComplete();
    }
  };

  const handleClick = () => {
    // Check authentication before allowing file selection
    if (!isAuthenticated) {
      console.error('User not authenticated for file upload');
      return;
    }
    fileInputRef.current?.click();
  };

  const quotaPercentage = userQuota 
    ? (userQuota.usage / userQuota.limit) * 100 
    : 0;
  
  const isNearQuotaLimit = quotaPercentage > 80;

  return (
    <>
      {isDraggingOverDocument && (
        <div 
          className="document-drop-overlay"
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onDrop={handleOverlayDrop}
        >
          <div className="drop-overlay-content">
            <Upload size={80} className="drop-overlay-icon" />
            <h2>Drop files anywhere</h2>
            <p>Release to upload to your knowledge base</p>
            <p className="drop-overlay-subtitle">PDF, Images, Documents, Code files and more</p>
          </div>
        </div>
      )}

      {(showAlways || uploadQueue.length > 0 || isNearQuotaLimit) && (
        <div className="document-upload-zone-container">
          {showAlways && (
            <div
              className={`document-upload-zone ${isDragging ? 'dragging' : ''} ${!isAuthenticated ? 'auth-required' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleClick}
              data-testid="upload-zone"
            >
              {!isAuthenticated ? (
                <div className="upload-zone-content">
                  <Lock size={48} className="auth-icon" />
                  <h3>Sign in to upload documents</h3>
                  <p className="auth-message">
                    You need to be signed in to upload and manage your documents.
                  </p>
                </div>
              ) : (
                <div className="upload-zone-content">
                  <Upload size={48} className="upload-icon" />
                  <h3>Drop files here or click to upload</h3>
                  <p className="upload-instructions">
                    Supported formats: PDF, Images (JPG, PNG, GIF, WebP), Documents (DOC, DOCX, TXT, RTF), 
                    Spreadsheets (XLS, XLSX, CSV), Presentations (PPT, PPTX), Code files (PY, JS, TS, JAVA, etc.), 
                    Data (JSON, XML, YAML), Text (MD, TEX)
                  </p>
                  <p className="upload-constraints">
                    Maximum file size: 50MB
                  </p>
                </div>
              )}
            </div>
          )}

          {isNearQuotaLimit && userQuota && (
            <div className="quota-warning">
              <AlertCircle size={16} />
              <span>
                You're using {quotaPercentage.toFixed(0)}% of your storage quota 
                ({(userQuota.usage / 1024 / 1024).toFixed(0)}MB / {(userQuota.limit / 1024 / 1024).toFixed(0)}MB)
              </span>
            </div>
          )}

          {/* Minimal inline upload progress for tests and UX */}
          {uploadQueue.length > 0 && (
            <div className="upload-progress-list" data-testid="upload-progress">
              {uploadQueue.map((item: any) => (
                <div key={item.id} className="upload-item">
                  <span className="upload-file-name">{item?.file?.name || 'file'}</span>
                  <span className="upload-file-progress">{`${Math.floor(item?.progress ?? 0)}%`}</span>
                </div>
              ))}
            </div>
          )}

        </div>
      )}

      {showTypeModal && pendingFiles.length > 0 && (
        <DocumentTypeModal
          isOpen={showTypeModal}
          onClose={() => {
            setShowTypeModal(false);
            setPendingFiles([]);
          }}
          onSelect={handleTypeSelect}
          filename={pendingFiles.length === 1 ? pendingFiles[0].name : `${pendingFiles.length} files`}
        />
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept=".pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.webp,.json,.xml,.yaml,.yml,.py,.js,.ts,.java,.cpp,.c,.cs,.rb,.go,.rs,.html,.css,.jsx,.tsx,.vue,.php,.swift,.kt,.md,.markdown,.tex,.latex"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        data-testid="file-input-hidden"
      />
    </>
  );
};

export default DocumentUploadZone;
